import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { Search, RefreshCw, AlertTriangle, CheckCircle2, Send, Loader2, Terminal, Trash2, Ghost, MailX, Archive, MessageSquare } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_COLOUR = {
  completed: 'default', success: 'default',
  pending: 'outline', processing: 'outline',
  failed: 'destructive', refunded: 'secondary', refund_failed: 'destructive',
  needs_attention: 'destructive'   // fulfillment failed post-payment; use founder tool to recover
};

export default function AdminDiagnostics({ initialSubtab = 'trace', subtabKey }) {
  // Sync external prop → internal tab state. Using a derived render approach to
  // avoid the react-hooks/set-state-in-effect lint warning.
  const [internalSubtab, setInternalSubtab] = useState(initialSubtab);
  const [lastSubtabKey, setLastSubtabKey] = useState(subtabKey);
  if (subtabKey !== lastSubtabKey) {
    setLastSubtabKey(subtabKey);
    if (subtabKey) setInternalSubtab(subtabKey);
  }
  const activeSubtab = internalSubtab;
  const setActiveSubtab = setInternalSubtab;

  const [reference, setReference] = useState('');
  const [trace, setTrace] = useState(null);
  const [tracing, setTracing] = useState(false);

  const [recoverOpen, setRecoverOpen] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState('');
  const [forceStatus, setForceStatus] = useState(false);
  const [recovering, setRecovering] = useState(false);

  // Orphan cleanup state
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphanPreview, setOrphanPreview] = useState(null); // { event, counts }
  const [orphanCleaning, setOrphanCleaning] = useState(false);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);

  const fetchOrphans = async () => {
    setOrphansLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/diagnostics/orphaned-events`);
      setOrphans(res.data.orphans || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load orphans');
    } finally { setOrphansLoading(false); }
  };

  const previewOrphanCleanup = async (ev) => {
    setOrphanPreview({ event: ev, counts: null, loading: true });
    setOrphanDialogOpen(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/diagnostics/cleanup-orphan-event/${encodeURIComponent(ev.event_id)}`,
        { dry_run: true }
      );
      setOrphanPreview({ event: ev, counts: res.data.related_counts, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
      setOrphanPreview(null);
      setOrphanDialogOpen(false);
    }
  };

  const confirmOrphanCleanup = async () => {
    if (!orphanPreview?.event) return;
    setOrphanCleaning(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/diagnostics/cleanup-orphan-event/${encodeURIComponent(orphanPreview.event.event_id)}`,
        { confirm: true }
      );
      const totalRelated = Object.values(res.data.related_deleted || {}).reduce(
        (s, v) => (typeof v === 'number' ? s + v : s), 0
      );
      toast.success(`Deleted "${res.data.event_title}" + ${totalRelated} related doc(s)`);
      setOrphanDialogOpen(false);
      setOrphanPreview(null);
      fetchOrphans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally { setOrphanCleaning(false); }
  };

  // Abandoned-cleanup state
  const [abPreview, setAbPreview] = useState(null); // { scanned, would_suppress, rows }
  const [abLoading, setAbLoading] = useState(false);
  const [abCommitting, setAbCommitting] = useState(false);
  const [abConfirmOpen, setAbConfirmOpen] = useState(false);

  const fetchAbandonedPreview = async () => {
    setAbLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/diagnostics/cleanup-abandoned-already-paid`,
        { dry_run: true }
      );
      setAbPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setAbLoading(false); }
  };

  const commitAbandonedCleanup = async () => {
    setAbCommitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/diagnostics/cleanup-abandoned-already-paid`,
        { confirm: true }
      );
      toast.success(`Flipped ${res.data.modified} row(s) to completed (matched ${res.data.matched})`);
      setAbConfirmOpen(false);
      // Refresh preview — should now show 0 matches
      fetchAbandonedPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally { setAbCommitting(false); }
  };

  // Archive-impact-window state (pre-deploy safety probe)
  const [archiveWindow, setArchiveWindow] = useState(null);
  const [archiveWindowLoading, setArchiveWindowLoading] = useState(false);

  const fetchArchiveWindow = async () => {
    setArchiveWindowLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/diagnostics/archive-impact-window`);
      setArchiveWindow(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch archive impact window');
    } finally { setArchiveWindowLoading(false); }
  };

  // Termii SMS sender-status state
  const [termiiStatus, setTermiiStatus] = useState(null);
  const [termiiLoading, setTermiiLoading] = useState(false);

  const fetchTermiiStatus = async () => {
    setTermiiLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/diagnostics/termii-sender-status`);
      setTermiiStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch Termii status');
    } finally { setTermiiLoading(false); }
  };

  // Log viewer state
  const [logPattern, setLogPattern] = useState('');
  const [logLevel, setLogLevel] = useState('all');
  const [logLimit, setLogLimit] = useState(100);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsAutoRefresh, setLogsAutoRefresh] = useState(false);
  const [logsDebug, setLogsDebug] = useState(null);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = { limit: logLimit };
      if (logPattern.trim()) params.pattern = logPattern.trim();
      if (logLevel && logLevel !== 'all') params.level = logLevel;
      const res = await axios.get(`${API_URL}/api/admin/diagnostics/logs`, { params });
      setLogs(res.data.lines || []);
      setLogsDebug({
        files: res.data.files_inspected || [],
        total: res.data.total_lines_in_window || 0,
        shown: res.data.count || 0,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Log fetch failed');
    } finally { setLogsLoading(false); }
  };

  const handleTrace = async () => {
    const ref = reference.trim();
    if (!ref) { toast.error('Enter a transaction reference'); return; }
    setTracing(true);
    setTrace(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/diagnostics/transaction-trace/${encodeURIComponent(ref)}`);
      setTrace(res.data);
      // Pre-fill override email from the transaction if present
      const em = res.data?.transaction?.guest_email || res.data?.transaction?.user_email || '';
      setOverrideEmail(em);
      // Suggest force_status if the txn is stuck
      const status = res.data?.transaction?.status;
      setForceStatus(status === 'pending' || status === 'processing');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Trace failed');
    } finally { setTracing(false); }
  };

  const handleRecover = async () => {
    if (!trace?.transaction) return;
    const ref = trace.transaction.reference || reference.trim();
    setRecovering(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/diagnostics/transaction-reissue/${encodeURIComponent(ref)}`,
        {
          confirm: true,
          force_status: !!forceStatus,
          override_email: overrideEmail.trim() || null
        }
      );
      // Surface skipped count separately — the backend now guarantees ticket_count === 0
      // will surface as an HTTPException (caught below), so this success branch only
      // runs when at least one ticket was created. But partial-skip (some good, some
      // stale ticket_type_id) still lands here — the toast must NOT read as pure success.
      const issued = res.data.ticket_count || 0;
      const skippedQty = res.data.skipped_count ?? (res.data.skipped || []).reduce(
        (s, o) => s + (o.quantity || 1), 0
      );
      const email = res.data.recipient_email;
      if (skippedQty > 0) {
        toast.warning(
          `Issued ${issued} ticket(s) to ${email} — but ${skippedQty} order(s) SKIPPED (ticket_type_id no longer exists). Review the skipped list before closing this reference.`,
          { duration: 10000 }
        );
      } else {
        toast.success(`Issued ${issued} ticket(s) to ${email}`);
      }
      setRecoverOpen(false);
      // Re-trace to show new state
      await handleTrace();
    } catch (err) {
      const code = err.response?.status;
      const detail = err.response?.data?.detail;
      // Structured detail (Fix 1 total-drop case) — surface the skipped list, not just a plain message.
      if (detail && typeof detail === 'object' && detail.error === 'fulfillment_failed') {
        const skippedQty = (detail.skipped || []).reduce((s, o) => s + (o.quantity || 1), 0);
        toast.error(
          `${code} FULFILLMENT FAILED — 0 tickets issued, ${skippedQty} order(s) skipped. ` +
          `Reason: ${detail.reason}. ${detail.next_step || ''}`,
          { duration: 12000 }
        );
      } else {
        toast.error(`${code || ''} ${typeof detail === 'string' ? detail : (detail?.reason || 'Recovery failed')}`);
      }
    } finally { setRecovering(false); }
  };

  const txn = trace?.transaction;
  const hasTickets = (trace?.ticket_count || 0) > 0;
  const canRecover = !!txn && !hasTickets;

  return (
    <div className="space-y-4" data-testid="admin-diagnostics-tab">
      <Tabs value={activeSubtab} onValueChange={setActiveSubtab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="trace" data-testid="diag-subtab-trace"><Search className="w-4 h-4 mr-1" />Transaction Trace</TabsTrigger>
          <TabsTrigger value="orphans" data-testid="diag-subtab-orphans"><Ghost className="w-4 h-4 mr-1" />Orphans</TabsTrigger>
          <TabsTrigger value="abandoned" data-testid="diag-subtab-abandoned"><MailX className="w-4 h-4 mr-1" />Abandoned Cleanup</TabsTrigger>
          <TabsTrigger value="archive-window" data-testid="diag-subtab-archive-window"><Archive className="w-4 h-4 mr-1" />Archive Impact</TabsTrigger>
          <TabsTrigger value="logs" data-testid="diag-subtab-logs"><Terminal className="w-4 h-4 mr-1" />Live Logs</TabsTrigger>
          <TabsTrigger value="sms" data-testid="diag-subtab-sms"><MessageSquare className="w-4 h-4 mr-1" />SMS Sender</TabsTrigger>
        </TabsList>
        <TabsContent value="trace" className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Search className="w-5 h-5" /> Transaction Diagnostics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Paystack reference (e.g. 55i1fgrgb7) or Stripe transaction ID"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTrace()}
              className="flex-1"
              data-testid="diagnostics-reference-input"
            />
            <Button onClick={handleTrace} disabled={tracing} data-testid="diagnostics-trace-btn">
              {tracing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              {tracing ? 'Tracing…' : 'Trace'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Read-only trace of a transaction&apos;s payment record, ticket creation, referral commission, and security events.
          </p>
        </CardContent>
      </Card>

      {trace && (
        <>
          {/* Diagnosis Banner */}
          {trace.diagnosis?.length > 0 && (
            <Alert data-testid="diagnostics-diagnosis">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="space-y-1 text-sm">
                  {trace.diagnosis.map((line, i) => (
                    <li key={i} className={line.includes('CRITICAL') ? 'text-destructive font-medium' : ''}>• {line}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Transaction Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Transaction</span>
                {txn && (
                  <div className="flex items-center gap-2">
                    <Badge variant={STATUS_COLOUR[txn.status] || 'outline'}>{txn.status}</Badge>
                    {canRecover && (
                      <Button size="sm" variant="default" onClick={() => setRecoverOpen(true)} data-testid="diagnostics-recover-btn">
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Recover Transaction
                      </Button>
                    )}
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!txn ? (
                <p className="text-sm text-muted-foreground">No transaction record found.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Reference:</span> <code className="text-xs">{txn.reference}</code></div>
                  <div><span className="text-muted-foreground">Event:</span> <code className="text-xs">{txn.event_id}</code></div>
                  <div><span className="text-muted-foreground">Email:</span> {txn.guest_email || txn.user_email || '—'}</div>
                  <div><span className="text-muted-foreground">Amount:</span> {txn.currency || 'NGN'} {(txn.amount || 0).toLocaleString()}</div>
                  <div><span className="text-muted-foreground">Tickets in DB:</span> <strong>{trace.ticket_count}</strong></div>
                  <div><span className="text-muted-foreground">Has referral:</span> {txn.has_referral ? 'Yes' : 'No'}</div>
                  {txn.skipped_orders && (
                    <div className="col-span-2 text-destructive">
                      <strong>Skipped orders:</strong> <code className="text-xs">{JSON.stringify(txn.skipped_orders)}</code>
                    </div>
                  )}
                  {txn.last_verify_error && (
                    <div className="col-span-2 text-destructive">
                      <strong>Last verify error:</strong> <code className="text-xs">{txn.last_verify_error}</code>
                    </div>
                  )}
                  {txn.manual_reissue_at && (
                    <div className="col-span-2 text-amber-500">
                      <CheckCircle2 className="w-3 h-3 inline" /> Manually reissued at {new Date(txn.manual_reissue_at).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tickets */}
          {trace.tickets?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Tickets ({trace.tickets.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left p-2">Ticket ID</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-center p-2">Status</th>
                      <th className="text-center p-2">Used</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {trace.tickets.map(t => (
                        <tr key={t.ticket_id}>
                          <td className="p-2 text-xs font-mono">{t.ticket_id}</td>
                          <td className="p-2 text-xs">{t.ticket_type_name}</td>
                          <td className="p-2 text-center"><Badge variant="outline">{t.status}</Badge></td>
                          <td className="p-2 text-center">{t.qr_code_used ? '✓' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Commissions */}
          {trace.commissions?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Referral Commissions ({trace.commissions.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="text-xs space-y-1">
                  {trace.commissions.map(c => (
                    <div key={c.commission_id} className="flex justify-between p-2 border rounded">
                      <span className="font-mono">{c.commission_id}</span>
                      <span>{c.currency} {(c.commission_amount || 0).toLocaleString()}</span>
                      <Badge variant="outline">{c.status}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Recover Dialog */}
      <Dialog open={recoverOpen} onOpenChange={setRecoverOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recover Transaction</DialogTitle>
            <DialogDescription>
              Issue {(txn?.tickets || []).reduce((s, t) => s + (t.quantity || 1), 0) || 'the missing'} ticket(s) for reference <code className="text-xs">{txn?.reference}</code>. This action is idempotent — running it twice will not duplicate tickets.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="rec-email">Recipient email</Label>
              <Input id="rec-email" value={overrideEmail} onChange={(e) => setOverrideEmail(e.target.value)} placeholder="customer@example.com" data-testid="recover-email-input" />
              <p className="text-xs text-muted-foreground mt-1">Leave as-is to use the email on the transaction.</p>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={forceStatus} onChange={(e) => setForceStatus(e.target.checked)} className="mt-1" data-testid="recover-force-status" />
              <span>
                <strong>Force status</strong> — override the status check.
                <br/>
                <span className="text-xs text-muted-foreground">
                  Only enable this AFTER manually verifying the payment was captured at Paystack/Stripe. Otherwise you may give away tickets for an unpaid transaction.
                </span>
              </span>
            </label>
            {txn && (txn.status === 'pending' || txn.status === 'processing') && !forceStatus && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>This transaction is <strong>{txn.status}</strong>. You must enable Force Status to recover it.</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRecoverOpen(false)} disabled={recovering}>Cancel</Button>
            <Button onClick={handleRecover} disabled={recovering} data-testid="recover-confirm-btn">
              {recovering ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              {recovering ? 'Recovering…' : 'Issue Tickets'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="orphans" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><Ghost className="w-5 h-5" /> Orphaned Events</span>
                <Button size="sm" variant="outline" onClick={fetchOrphans} disabled={orphansLoading} data-testid="orphans-refresh-btn">
                  {orphansLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Events whose organizer_id no longer maps to any user. These can&apos;t be reassigned (no phone/email match) and should be deleted. Cleanup also purges related tickets, transactions, referrals, broadcasts, coupons, scans, and abandoned payments tied to the event.
              </p>
              {orphans.length === 0 ? (
                <div className="text-sm text-muted-foreground p-6 text-center border rounded">
                  {orphansLoading ? 'Loading…' : '— click Refresh to scan for orphans —'}
                </div>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left p-2">Event ID</th>
                      <th className="text-left p-2">Title</th>
                      <th className="text-left p-2">Organizer (orphaned)</th>
                      <th className="text-right p-2">Actions</th>
                    </tr></thead>
                    <tbody className="divide-y" data-testid="orphans-table-body">
                      {orphans.map(ev => (
                        <tr key={ev.event_id}>
                          <td className="p-2 text-xs font-mono">{ev.event_id}</td>
                          <td className="p-2">{ev.title || '—'}</td>
                          <td className="p-2 text-xs">
                            <code>{ev.organizer_id || 'none'}</code>
                            {ev.organizer_name && <span className="text-muted-foreground"> · {ev.organizer_name}</span>}
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => previewOrphanCleanup(ev)}
                              data-testid={`orphan-cleanup-btn-${ev.event_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Cleanup
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={orphanDialogOpen} onOpenChange={(o) => { setOrphanDialogOpen(o); if (!o) setOrphanPreview(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete orphan event</DialogTitle>
                <DialogDescription>
                  Permanently delete <strong>{orphanPreview?.event?.title || orphanPreview?.event?.event_id}</strong> and all related records. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                {orphanPreview?.loading ? (
                  <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Counting related records…</div>
                ) : orphanPreview?.counts ? (
                  <>
                    <p className="text-xs text-muted-foreground">Related records to be deleted:</p>
                    <ul className="text-xs grid grid-cols-2 gap-1 font-mono border rounded p-2 max-h-60 overflow-y-auto" data-testid="orphan-preview-counts">
                      {Object.entries(orphanPreview.counts).map(([coll, n]) => (
                        <li key={coll} className="flex justify-between gap-2">
                          <span className="text-muted-foreground truncate">{coll}</span>
                          <span className={n > 0 ? 'text-destructive font-bold' : ''}>{n}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-foreground">+ 1 event document</p>
                  </>
                ) : null}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOrphanDialogOpen(false)} disabled={orphanCleaning}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={confirmOrphanCleanup}
                  disabled={orphanCleaning || orphanPreview?.loading}
                  data-testid="orphan-confirm-delete-btn"
                >
                  {orphanCleaning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  {orphanCleaning ? 'Deleting…' : 'Delete permanently'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="abandoned" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><MailX className="w-5 h-5" /> Abandoned reminders &mdash; already-paid customers</span>
                <Button size="sm" variant="outline" onClick={fetchAbandonedPreview} disabled={abLoading} data-testid="abandoned-preview-btn">
                  {abLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Preview
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Scans in-flight abandoned-cart reminders (status=&quot;abandoned&quot;) and finds rows whose customer
                has already purchased the same event via a different path — fresh Paystack reference, Stripe
                checkout, or admin-issued ticket. Uses the same gate the live scheduler uses pre-send, so this
                is a backlog mop-up. Click <strong>Preview</strong> first to see what would be suppressed.
              </p>
              {!abPreview ? (
                <div className="text-sm text-muted-foreground p-6 text-center border rounded">
                  &mdash; click Preview to scan &mdash;
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="border rounded p-3">
                      <p className="text-xs text-muted-foreground">In-flight abandoned rows scanned</p>
                      <p className="text-2xl font-bold" data-testid="abandoned-scanned-count">{abPreview.scanned}</p>
                    </div>
                    <div className="border rounded p-3 bg-amber-500/5 border-amber-500/30">
                      <p className="text-xs text-muted-foreground">Already-paid (would be suppressed)</p>
                      <p className="text-2xl font-bold text-amber-500" data-testid="abandoned-suppress-count">{abPreview.would_suppress}</p>
                    </div>
                  </div>
                  {abPreview.rows.length > 0 && (
                    <div className="border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50"><tr>
                          <th className="text-left p-2">Reference</th>
                          <th className="text-left p-2">Customer email</th>
                          <th className="text-left p-2">Event</th>
                          <th className="text-right p-2">Reminders sent</th>
                          <th className="text-left p-2">Created</th>
                        </tr></thead>
                        <tbody className="divide-y" data-testid="abandoned-rows-tbody">
                          {abPreview.rows.map(r => (
                            <tr key={r.reference}>
                              <td className="p-2 text-xs font-mono">{r.reference}</td>
                              <td className="p-2 text-xs">{r.customer_email}</td>
                              <td className="p-2 text-xs">
                                <div className="truncate max-w-[200px]">{r.event_title || '—'}</div>
                                <div className="text-muted-foreground truncate max-w-[200px] font-mono">{r.event_id}</div>
                              </td>
                              <td className="p-2 text-right">{r.reminder_count}</td>
                              <td className="p-2 text-xs text-muted-foreground">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {abPreview.would_suppress > 0 ? (
                    <div className="flex justify-end pt-2">
                      <Button
                        variant="destructive"
                        onClick={() => setAbConfirmOpen(true)}
                        disabled={abCommitting}
                        data-testid="abandoned-commit-btn"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Flip {abPreview.would_suppress} row{abPreview.would_suppress !== 1 ? 's' : ''} to completed
                      </Button>
                    </div>
                  ) : (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        Nothing to clean up. The pre-send gate is keeping the backlog clean.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Dialog open={abConfirmOpen} onOpenChange={setAbConfirmOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Commit cleanup</DialogTitle>
                <DialogDescription>
                  Flip <strong>{abPreview?.would_suppress || 0} row(s)</strong> to status=&quot;completed&quot;.
                  These customers have already purchased — no further reminders will be sent.
                  All flipped rows will be tagged <code>completed_via=&quot;admin_diagnostics_cleanup&quot;</code> for audit.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAbConfirmOpen(false)} disabled={abCommitting}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={commitAbandonedCleanup}
                  disabled={abCommitting}
                  data-testid="abandoned-confirm-commit-btn"
                >
                  {abCommitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  {abCommitting ? 'Committing…' : 'Commit'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="archive-window" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><Archive className="w-5 h-5" /> Archive impact window</span>
                <Button size="sm" variant="outline" onClick={fetchArchiveWindow} disabled={archiveWindowLoading} data-testid="archive-window-fetch-btn">
                  {archiveWindowLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Run check
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Pre-deploy safety probe. Lists events whose <code>end_date</code> is between 2 hours and 7 days ago, are
                not yet archived, and are not recurring. These would have been visible under the old 7-day grace logic
                but will be archived within an hour of the timezone-aware logic running. Read-only, safe to run anytime.
              </p>
              {!archiveWindow ? (
                <div className="text-sm text-muted-foreground p-6 text-center border rounded">
                  &mdash; click Run check to scan &mdash;
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="border rounded p-3">
                      <p className="text-muted-foreground">Window start (7 days ago, UTC)</p>
                      <p className="font-mono mt-1">{archiveWindow.seven_days_ago?.slice(0, 19)}</p>
                    </div>
                    <div className="border rounded p-3">
                      <p className="text-muted-foreground">Window end (2 hours ago, UTC)</p>
                      <p className="font-mono mt-1">{archiveWindow.two_hours_ago?.slice(0, 19)}</p>
                    </div>
                  </div>
                  <div className={`border rounded p-3 text-sm ${archiveWindow.count === 0 ? 'bg-green-500/5 border-green-500/30' : 'bg-amber-500/5 border-amber-500/30'}`}>
                    <div className="flex items-center gap-2">
                      {archiveWindow.count === 0 ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                      <p className="font-medium" data-testid="archive-window-count">
                        {archiveWindow.count === 0
                          ? 'No events in the impact window. Safe to deploy freely.'
                          : `${archiveWindow.count} event${archiveWindow.count !== 1 ? 's' : ''} would archive within an hour of redeploy.`}
                      </p>
                    </div>
                  </div>
                  {archiveWindow.rows?.length > 0 && (
                    <div className="border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50"><tr>
                          <th className="text-left p-2">Event</th>
                          <th className="text-left p-2">End date</th>
                          <th className="text-left p-2">Timezone</th>
                          <th className="text-left p-2">Organizer</th>
                          <th className="text-left p-2">Published</th>
                        </tr></thead>
                        <tbody className="divide-y" data-testid="archive-window-rows-tbody">
                          {archiveWindow.rows.map(r => (
                            <tr key={r.event_id}>
                              <td className="p-2">
                                <div className="truncate max-w-[220px]">{r.title || '—'}</div>
                                <div className="text-xs text-muted-foreground font-mono truncate max-w-[220px]">{r.event_id}</div>
                              </td>
                              <td className="p-2 text-xs font-mono">{(r.end_date || '').slice(0, 19).replace('T', ' ')}</td>
                              <td className="p-2 text-xs">{r.timezone || <span className="text-muted-foreground italic">null</span>}</td>
                              <td className="p-2 text-xs">
                                <div className="truncate max-w-[200px]">{r.organizer_name || '—'}</div>
                                <div className="text-muted-foreground truncate max-w-[200px]">{r.organizer_email || ''}</div>
                              </td>
                              <td className="p-2 text-xs">{r.is_published ? 'yes' : 'no'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Terminal className="w-5 h-5" /> Live Backend Logs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_100px_auto] gap-2">
                <Input
                  placeholder="Filter pattern (e.g. ABANDONED_POLL, [WEBHOOK], 55i1fgrgb7)"
                  value={logPattern}
                  onChange={(e) => setLogPattern(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                  data-testid="logs-pattern-input"
                />
                <Select value={logLevel} onValueChange={setLogLevel}>
                  <SelectTrigger data-testid="logs-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All levels</SelectItem>
                    <SelectItem value="INFO">INFO</SelectItem>
                    <SelectItem value="WARNING">WARNING</SelectItem>
                    <SelectItem value="ERROR">ERROR</SelectItem>
                    <SelectItem value="CRITICAL">CRITICAL</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={String(logLimit)} onValueChange={(v) => setLogLimit(Number(v))}>
                  <SelectTrigger data-testid="logs-limit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="250">250</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={fetchLogs} disabled={logsLoading} data-testid="logs-fetch-btn">
                  {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={logsAutoRefresh} onChange={(e) => {
                    const on = e.target.checked;
                    setLogsAutoRefresh(on);
                    if (on) {
                      fetchLogs();
                      // auto-refresh every 10s — store interval on window so the next toggle clears it
                      if (window.__logsRefreshInterval) clearInterval(window.__logsRefreshInterval);
                      window.__logsRefreshInterval = setInterval(fetchLogs, 10000);
                    } else if (window.__logsRefreshInterval) {
                      clearInterval(window.__logsRefreshInterval);
                      window.__logsRefreshInterval = null;
                    }
                  }} data-testid="logs-autorefresh" />
                  Auto-refresh every 10s
                </label>
                <span>•</span>
                <span>{logs.length} lines shown</span>
              </div>
              {logsDebug && (
                <details className="text-xs text-muted-foreground border rounded p-2" data-testid="logs-debug-info">
                  <summary className="cursor-pointer font-medium">Log source debug ({logsDebug.total} total lines in window, {logsDebug.shown} shown)</summary>
                  <ul className="mt-2 space-y-0.5 font-mono">
                    {logsDebug.files.map((f, i) => (
                      <li key={i} className={f.exists ? 'text-green-500' : 'text-muted-foreground/50'}>
                        {f.exists ? '✓' : '✗'} {f.path}{f.exists ? ` (${(f.size_bytes / 1024).toFixed(1)} KB)` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <pre className="bg-black/90 text-green-400 font-mono text-xs p-4 rounded-lg overflow-x-auto max-h-[600px] overflow-y-auto" data-testid="logs-output">
{logs.length === 0 ? '— click refresh to load logs —' : logs.join('\n')}
              </pre>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- SMS / Termii Sender Status ---------------- */}
        <TabsContent value="sms" className="space-y-4">
          <Card data-testid="termii-status-card">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" /> Termii SMS Sender
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Live status of the Sender IDs registered on the connected Termii account.
                    Until your active sender shows <strong>approved</strong>, OTP delivery will fail.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchTermiiStatus}
                  disabled={termiiLoading}
                  data-testid="termii-refresh-btn"
                >
                  {termiiLoading
                    ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    : <RefreshCw className="w-4 h-4 mr-1" />}
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!termiiStatus && (
                <p className="text-sm text-muted-foreground" data-testid="termii-empty-hint">
                  Click <em>Refresh</em> to fetch the current state from Termii.
                </p>
              )}

              {termiiStatus && (
                <>
                  {/* Headline state */}
                  <div className="flex items-center gap-2" data-testid="termii-headline">
                    {termiiStatus.error ? (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Error: {termiiStatus.error}
                      </Badge>
                    ) : !termiiStatus.configured ? (
                      <Badge variant="destructive">Not configured (TERMII_API_KEY missing)</Badge>
                    ) : termiiStatus.configured_sender_approved ? (
                      <Badge variant="default" className="bg-green-600 hover:bg-green-700 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Sender <strong className="font-semibold">{termiiStatus.configured_sender}</strong> approved — OTP will deliver
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-300 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Active sender <strong className="font-semibold">{termiiStatus.configured_sender}</strong> not yet approved
                      </Badge>
                    )}
                  </div>

                  {/* Per-sender table */}
                  {termiiStatus.senders && termiiStatus.senders.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden" data-testid="termii-senders-table">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr className="text-left">
                            <th className="p-2 font-medium">Sender</th>
                            <th className="p-2 font-medium">Status</th>
                            <th className="p-2 font-medium">Country</th>
                            <th className="p-2 font-medium">Company</th>
                            <th className="p-2 font-medium">Active?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {termiiStatus.senders.map((s, idx) => (
                            <tr key={s.name || idx} className="border-t" data-testid={`termii-sender-row-${s.name}`}>
                              <td className="p-2 font-mono">{s.name || '—'}</td>
                              <td className="p-2">
                                <Badge
                                  variant={(s.status || '').toLowerCase() === 'approved' ? 'default' : 'outline'}
                                  className={(s.status || '').toLowerCase() === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                                  data-testid={`termii-sender-status-${s.name}`}
                                >
                                  {s.status || 'unknown'}
                                </Badge>
                              </td>
                              <td className="p-2 text-muted-foreground">{s.country || '—'}</td>
                              <td className="p-2 text-muted-foreground">{s.company || '—'}</td>
                              <td className="p-2">
                                {s.in_use
                                  ? <Badge variant="secondary">In use</Badge>
                                  : <span className="text-muted-foreground">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    !termiiStatus.error && (
                      <Alert>
                        <AlertDescription>
                          No Sender IDs found on this Termii account. Register one in the Termii dashboard
                          and set <code>TERMII_SENDER_ID</code> to the approved name.
                        </AlertDescription>
                      </Alert>
                    )
                  )}

                  <p className="text-xs text-muted-foreground" data-testid="termii-fetched-at">
                    Fetched at: {termiiStatus.fetched_at}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
